import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import PiModal from "../../../components/PiModal";
import {
  getRegisteredPowerSlideTemplates,
  PowerSlideTemplate,
  PowerSlideTemplateId,
} from "../../../models/interactions/PowerSlideTemplates";
import { getPowerSlideSurface } from "./PowerSlideSurfaces";
import {
  DEFAULT_POWER_SLIDE_THEME,
  PowerSlideTheme,
} from "../../../models/interactions/PowerSlideTheme";

interface PowerSlideTemplatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (templateId: PowerSlideTemplateId) => void;
  /** The slide being retemplated, if this is a change rather than an add. */
  currentTemplateId?: PowerSlideTemplateId;
  theme?: PowerSlideTheme;
}

/**
 * The template's Runtime variant, exported straight from Figma. The surface
 * colour sits behind it so a slow or failed load still reads as the right kind
 * of slide rather than an empty box.
 */
function PowerSlideTemplateThumb({
  template,
  alt,
  theme,
}: {
  template: PowerSlideTemplate;
  alt: string;
  theme: PowerSlideTheme;
}) {
  const surface = getPowerSlideSurface(template.surface, theme);

  return (
    <div
      className="aspect-video w-full overflow-hidden"
      style={{ backgroundColor: surface.background }}
    >
      <img
        src={`/powerslide_previews/${template.previewImage}`}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </div>
  );
}

const GRID_GAP = 20;
/** Card chrome under the thumbnail: border, padding, gap and the label line. */
const CARD_CHROME_HEIGHT = 44;
const MIN_CARD_WIDTH = 150;

/**
 * The widest card that still lets every template fit without scrolling, trying
 * four columns first and dropping to two when the box is too narrow for them.
 */
export function fitGrid(
  width: number,
  height: number,
  count: number
): { columns: number; cardWidth: number } {
  const candidates = [4, 3, 2];

  for (const columns of candidates) {
    const rows = Math.ceil(count / columns);
    const byWidth = (width - (columns - 1) * GRID_GAP) / columns;
    const rowHeight = (height - (rows - 1) * GRID_GAP) / rows;
    // A card is a 16:9 thumbnail plus its chrome, so height caps width too.
    const byHeight = ((rowHeight - CARD_CHROME_HEIGHT) * 16) / 9;
    const cardWidth = Math.floor(Math.min(byWidth, byHeight));
    if (cardWidth >= MIN_CARD_WIDTH) return { columns, cardWidth };
  }

  // Nothing fits: keep two columns and let the container scroll.
  return { columns: 2, cardWidth: MIN_CARD_WIDTH };
}

export default function PowerSlideTemplatePicker(
  props: PowerSlideTemplatePickerProps
) {
  const {
    isOpen,
    onClose,
    onSelect,
    currentTemplateId,
    theme = DEFAULT_POWER_SLIDE_THEME,
  } = props;
  const { t } = useTranslation();
  const templates = getRegisteredPowerSlideTemplates();
  const gridRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ columns: 4, cardWidth: MIN_CARD_WIDTH });

  useLayoutEffect(() => {
    const element = gridRef.current;
    if (!isOpen || !element) return;

    const measure = () =>
      setLayout(
        fitGrid(element.clientWidth, element.clientHeight, templates.length)
      );
    measure();

    // Not available in jsdom, so tests fall back to the initial measurement.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isOpen, templates.length]);

  // No group headers: with large, visually distinct previews, reading order
  // across the grid beats categorisation, and a 6/6 split lands mid-row.
  const cards = templates.map((template) => {
    const name = t(`powerslide.templates.${template.locId}`, {
      defaultValue: template.name,
    });

    return (
      <button
        key={template.id}
        type="button"
        aria-label={`powerslide-template-${template.id}`}
        onClick={() => {
          onSelect(template.id);
          onClose();
        }}
        className={classNames(
          "group flex flex-col gap-[8px] rounded-[6px] border-2 border-solid p-[6px] text-left hover:border-creator-primary",
          template.id === currentTemplateId
            ? "border-creator-primary"
            : "border-transparent"
        )}
      >
        <div className="overflow-hidden rounded-[4px] border border-solid border-creator-border">
          <PowerSlideTemplateThumb template={template} alt={name} theme={theme} />
        </div>
        <span className="text-[13px] font-semibold text-creator-text group-hover:text-creator-primary">
          {name}
        </span>
      </button>
    );
  });

  return (
    <PiModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("powerslide.picker.title", { defaultValue: "Add a slide" })}
      width="min(1104px, 92vw)"
      height="min(680px, 88vh)"
    >
      <div className="flex h-full flex-col gap-[12px]">
        <p className="text-[14px] text-creator-text-sub">
          {t("powerslide.picker.subtitle", {
            defaultValue: "Choose a template to start from",
          })}
        </p>
        {/*
          Sized from the box rather than from a breakpoint: at anything under
          the design's 1640px the twelve cards would otherwise need a
          scrollbar, and a picker you have to scroll defeats "choose by
          looking". overflow-auto stays as a floor for very small windows.
        */}
        <div ref={gridRef} className="flex min-h-0 flex-1 justify-center overflow-auto">
          <div
            className="grid content-start"
            style={{
              gap: GRID_GAP,
              gridTemplateColumns: `repeat(${layout.columns}, ${layout.cardWidth}px)`,
            }}
          >
            {cards}
          </div>
        </div>
      </div>
    </PiModal>
  );
}
